import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RotateCcw} from 'lucide-react';
import { loadEphemerisData, findClosestEphemerisEntry } from '../../astro/ephemerisUtils';

const RetrogradeExperience = () => {
  const [retrogradeData, setRetrogradeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nextRetrogrades, setNextRetrogrades] = React.useState({});

  useEffect(() => {
    calculateRetrogrades();
  }, []);

  useEffect(() => {
    const fetchNextRetrogrades = async () => {
      const results = {};
      for (const planet of retrogradeData.retrogrades) {
        if (!planet.isRetrograde) {
          const maxDays = 1095; // 3 years
          let found = false;
          let now = new Date();
          let searchDate = new Date(now);
          for (let daysForward = 1; daysForward <= maxDays; daysForward++) {
            searchDate.setDate(now.getDate() + daysForward);
            const testYear = searchDate.getFullYear();
            let testEphemerisData = await loadEphemerisData(testYear);
            if (testEphemerisData) {
              const entry = findClosestEphemerisEntry(testEphemerisData, searchDate);
              if (entry && entry.planets && entry.planets[planet.planet]) {
                // Check if this date is retrograde
                const prevDay = new Date(searchDate.getTime() - (24 * 60 * 60 * 1000));
                let prevEntry = entry;
                if (prevDay.getFullYear() !== testYear) {
                  const prevEphemerisData = await loadEphemerisData(prevDay.getFullYear());
                  prevEntry = findClosestEphemerisEntry(prevEphemerisData, prevDay);
                } else {
                  prevEntry = findClosestEphemerisEntry(testEphemerisData, prevDay);
                }
                if (prevEntry && prevEntry.planets && prevEntry.planets[planet.planet]) {
                  const prevLongitude = prevEntry.planets[planet.planet].longitude;
                  const currentLongitude = entry.planets[planet.planet].longitude;
                  const motion = (currentLongitude - prevLongitude + 360) % 360;
                  // Retrograde if motion > 180 degrees (backwards motion)
                  if (motion > 180) {
                    results[planet.planet] = formatDate(searchDate.toISOString().split('T')[0]);
                    found = true;
                    break;
                  }
                }
              }
            }
          }
          if (!found) results[planet.planet] = 'No retrograde soon';
        }
      }
      setNextRetrogrades(results);
    };
    if (retrogradeData && retrogradeData.retrogrades) {
      fetchNextRetrogrades();
    }
  }, [retrogradeData]);

  const calculateRetrogradeStatus = async (planetName, date) => {
    try {
      const year = date.getFullYear();
      
      // Load ephemeris data for the current year
      const ephemerisData = await loadEphemerisData(year);
      
      if (!ephemerisData) {
        console.warn(`No ephemeris data available for ${year}, using fallback calculation`);
        return { isRetrograde: false, startDate: null, endDate: null };
      }
      
      // Get positions for current date and surrounding days
      const positions = [];
      for (let i = -3; i <= 3; i++) {
        const testDate = new Date(date.getTime() + (i * 24 * 60 * 60 * 1000));
        const testYear = testDate.getFullYear();
        
        let testEphemerisData = ephemerisData;
        if (testYear !== year) {
          testEphemerisData = await loadEphemerisData(testYear);
        }
        
        if (testEphemerisData) {
          const entry = findClosestEphemerisEntry(testEphemerisData, testDate);
          if (entry && entry.planets && entry.planets[planetName]) {
            positions.push({
              date: testDate,
              longitude: entry.planets[planetName].longitude
            });
          }
        }
      }
      
      // Need at least 3 positions to calculate motion
      if (positions.length < 3) {
        console.warn(`Insufficient ephemeris data for ${planetName}`);
        return { isRetrograde: false, startDate: null, endDate: null };
      }
      
      // Find current position (middle of the array)
      const currentIndex = Math.floor(positions.length / 2);
      const currentLongitude = positions[currentIndex].longitude;
      const prevLongitude = positions[currentIndex - 1]?.longitude;
      const nextLongitude = positions[currentIndex + 1]?.longitude;
      
      if (prevLongitude === undefined || nextLongitude === undefined) {
        return { isRetrograde: false, startDate: null, endDate: null };
      }
      
      // Calculate apparent motion
      const motionBefore = (currentLongitude - prevLongitude + 360) % 360;
      const motionAfter = (nextLongitude - currentLongitude + 360) % 360;
      
      // Planet is retrograde when apparent motion is backwards (> 180 degrees)
      const isRetrograde = motionAfter > 180;
      
      // Calculate actual retrograde periods from ephemeris data
      let startDate = null;
      let endDate = null;
      
      if (isRetrograde) {
        // Look for retrograde start (when motion changes from direct to retrograde)
        const retrogradeStart = await findRetrogradeStart(planetName, date, ephemerisData);
        const retrogradeEnd = await findRetrogradeEnd(planetName, date, ephemerisData);
        
        startDate = retrogradeStart;
        endDate = retrogradeEnd;
      }
      
      return {
        isRetrograde,
        startDate,
        endDate
      };
    } catch (error) {
      console.error(`Error calculating retrograde for ${planetName}:`, error);
      return { isRetrograde: false, startDate: null, endDate: null };
    }
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return null;
    const date = new Date(isoDate);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Helper to determine if a planet is slow-moving
  const isSlowPlanet = (planetName) => ['Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto'].includes(planetName);

  const findRetrogradeStart = async (planetName, currentDate, ephemerisData) => {
    try {
      // Look backwards from current date to find when retrograde started
      const maxDays = isSlowPlanet(planetName) ? 180 : 60;
      for (let daysBack = 1; daysBack <= maxDays; daysBack++) {
        const testDate = new Date(currentDate.getTime() - (daysBack * 24 * 60 * 60 * 1000));
        const testYear = testDate.getFullYear();
        
        let testEphemerisData = ephemerisData;
        if (testYear !== currentDate.getFullYear()) {
          testEphemerisData = await loadEphemerisData(testYear);
        }
        
        if (testEphemerisData) {
          const entry = findClosestEphemerisEntry(testEphemerisData, testDate);
          if (entry && entry.planets && entry.planets[planetName]) {
            // Check if this date was direct (not retrograde)
            const nextDay = new Date(testDate.getTime() + (24 * 60 * 60 * 1000));
            let nextEntry = entry;
            if (nextDay.getFullYear() !== testYear) {
              const nextEphemerisData = await loadEphemerisData(nextDay.getFullYear());
              nextEntry = findClosestEphemerisEntry(nextEphemerisData, nextDay);
            } else {
              nextEntry = findClosestEphemerisEntry(testEphemerisData, nextDay);
            }
            
            if (nextEntry && nextEntry.planets && nextEntry.planets[planetName]) {
              const currentLongitude = entry.planets[planetName].longitude;
              const nextLongitude = nextEntry.planets[planetName].longitude;
              const motion = (nextLongitude - currentLongitude + 360) % 360;
              
              // If motion is direct (≤ 180 degrees), this might be the start
              if (motion <= 180) {
                return formatDate(testDate.toISOString().split('T')[0]);
              }
            }
          }
        }
      }
      return null;
    } catch (error) {
      console.error(`Error finding retrograde start for ${planetName}:`, error);
      return null;
    }
  };

  const findRetrogradeEnd = async (planetName, currentDate, ephemerisData) => {
    try {
      // Look forwards from current date to find when retrograde ends
      const maxDays = isSlowPlanet(planetName) ? 180 : 60;
      for (let daysForward = 1; daysForward <= maxDays; daysForward++) {
        const testDate = new Date(currentDate.getTime() + (daysForward * 24 * 60 * 60 * 1000));
        const testYear = testDate.getFullYear();
        
        let testEphemerisData = ephemerisData;
        if (testYear !== currentDate.getFullYear()) {
          testEphemerisData = await loadEphemerisData(testYear);
        }
        
        if (testEphemerisData) {
          const entry = findClosestEphemerisEntry(testEphemerisData, testDate);
          if (entry && entry.planets && entry.planets[planetName]) {
            // Check if this date is direct (not retrograde)
            const prevDay = new Date(testDate.getTime() - (24 * 60 * 60 * 1000));
            let prevEntry = entry;
            if (prevDay.getFullYear() !== testYear) {
              const prevEphemerisData = await loadEphemerisData(prevDay.getFullYear());
              prevEntry = findClosestEphemerisEntry(prevEphemerisData, prevDay);
            } else {
              prevEntry = findClosestEphemerisEntry(testEphemerisData, prevDay);
            }
            
            if (prevEntry && prevEntry.planets && prevEntry.planets[planetName]) {
              const prevLongitude = prevEntry.planets[planetName].longitude;
              const currentLongitude = entry.planets[planetName].longitude;
              const motion = (currentLongitude - prevLongitude + 360) % 360;
              
              // If motion is direct (≤ 180 degrees), this might be the end
              if (motion <= 180) {
                return formatDate(testDate.toISOString().split('T')[0]);
              }
            }
          }
        }
      }
      return null;
    } catch (error) {
      console.error(`Error finding retrograde end for ${planetName}:`, error);
      return null;
    }
  };

  const calculateRetrogrades = async () => {
    const now = new Date();
    const currentDate = now.toISOString().split('T')[0];
    const currentTime = now.toTimeString().slice(0, 5);

    const planets = [
      {
        planet: 'Mercury',
        icon: '☿',
        description: 'Communication, technology, and travel may be affected. Double-check messages and avoid signing contracts.',
        keywords: ['Communication', 'Technology', 'Travel', 'Contracts'],
        color: '#87CEEB'
      },
      {
        planet: 'Venus',
        icon: '♀',
        description: 'Love, relationships, and beauty matters may be reconsidered. Past relationships may resurface.',
        keywords: ['Love', 'Relationships', 'Beauty', 'Harmony'],
        color: '#FF69B4'
      },
      {
        planet: 'Mars',
        icon: '🔴',
        description: 'Energy and action may feel blocked. Patience is required, and conflicts may arise.',
        keywords: ['Energy', 'Action', 'Conflict', 'Patience'],
        color: '#FF4444'
      },
      {
        planet: 'Jupiter',
        icon: '♃',
        description: 'Expansion and growth may slow down. Internal wisdom and spiritual growth are favored.',
        keywords: ['Expansion', 'Wisdom', 'Growth', 'Spirituality'],
        color: '#FFB347'
      },
      {
        planet: 'Saturn',
        icon: '♄',
        description: 'Structure and discipline may feel restrictive. Karmic lessons and past issues resurface.',
        keywords: ['Structure', 'Discipline', 'Karma', 'Lessons'],
        color: '#708090'
      },
      {
        planet: 'Uranus',
        icon: '♅',
        description: 'Innovation and rebellion may be internalized. Sudden changes may feel more personal.',
        keywords: ['Innovation', 'Rebellion', 'Change', 'Freedom'],
        color: '#00CED1'
      },
      {
        planet: 'Neptune',
        icon: '♆',
        description: 'Dreams and illusions may be questioned. Spiritual insights and artistic inspiration deepen.',
        keywords: ['Dreams', 'Illusions', 'Spirituality', 'Art'],
        color: '#4169E1'
      },
      {
        planet: 'Pluto',
        icon: '♇',
        description: 'Transformation and power dynamics may be internalized. Deep psychological work is favored.',
        keywords: ['Transformation', 'Power', 'Psychology', 'Depth'],
        color: '#800080'
      }
    ];

    // Calculate retrograde status for each planet using ephemeris data
    const retrogrades = [];
    for (const planet of planets) {
      const retrogradeStatus = await calculateRetrogradeStatus(planet.planet, now);
      retrogrades.push({
        ...planet,
        ...retrogradeStatus
      });
    }

    setRetrogradeData({
      currentDate,
      currentTime,
      retrogrades,
      totalRetrogrades: retrogrades.filter(r => r.isRetrograde).length
    });
    setLoading(false);
  };

  if (loading) {
    return (
      <div 
        className="full-screen-image-container full-height margin-top-0"
        style={{
          backgroundImage: `url('/img/saturnwallpaper.jpg')`,
        }}
      >
        <div className="ascendant-display-container">
          <div className="ascendant-header">
            <div className="ascendant-title">
              <RotateCcw size={24} />
              <h1>Current Retrogrades</h1>
            </div>
          </div>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <RotateCcw size={48} className="spinning" />
            <p style={{ marginTop: '1rem', color: '#FFD700' }}>Calculating planetary positions...</p>
          </div>
        </div>
      </div>
    );
  }

  // Add these color constants at the top (use your established colors)
  const RETROGRADE_COLOR = '#FF4444'; // your red
  const DIRECT_COLOR = '#43e97b'; // your green

  const sortedRetrogrades = [...retrogradeData.retrogrades].sort((a, b) => {
    if (a.isRetrograde === b.isRetrograde) return 0;
    return a.isRetrograde ? -1 : 1;
  });

  const solarSystemOrder = ['Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto'];
  const planetPairs = [
    ['Mercury', 'Pluto'],
    ['Venus', 'Mars'],
    ['Jupiter', 'Saturn'],
    ['Uranus', 'Neptune'],
  ];

  // Separate retrograde and direct planets, both in solar system order
  const retroByName = Object.fromEntries(sortedRetrogrades.map(p => [p.planet, p]));
  const retrogradePlanets = solarSystemOrder.filter(name => retroByName[name] && retroByName[name].isRetrograde);
  const directPlanets = solarSystemOrder.filter(name => retroByName[name] && !retroByName[name].isRetrograde);

  // Build the display pairs: retrograde pairs first, then direct pairs
  const displayPairs = [];
  for (const pair of planetPairs) {
    const retroPair = pair.filter(name => retrogradePlanets.includes(name));
    if (retroPair.length) displayPairs.push(retroPair);
  }
  for (const pair of planetPairs) {
    const directPair = pair.filter(name => directPlanets.includes(name));
    if (directPair.length) displayPairs.push(directPair);
  }

  return (
    <div 
      className="full-screen-image-container full-height margin-top-0"
      style={{
        backgroundImage: `url('/img/saturnwallpaper.jpg')`,
      }}
    >
      <motion.div
        key="result"
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 50 }}
        transition={{ duration: 0.4 }}
        className="ascendant-display-container"
      >
        <div className="ascendant-result">
          <div className="result-header">
            <h2>Current Planetary Retrogrades</h2>
            {/* Remove the old Active Retrogrades box */}

            {/* Details row with boxes: left = Date, center = X Active Retrogrades (gold), right = Time */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              margin: '1.5rem 0 2.5rem 0',
              gap: '1.5rem',
            }}>
              <div style={{
                flex: 1,
                padding: '1rem',
                borderRadius: '12px',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                fontSize: '1.1rem',
                color: '#fff',
                fontWeight: 500,
              }}>
                <span style={{ opacity: 0.7 }}>Date</span> <span style={{ fontWeight: 700 }}>{retrogradeData.currentDate}</span>
              </div>
              <div style={{
                flex: 1,
                padding: '1rem',
                borderRadius: '12px',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 215, 0, 0.4)',
                textAlign: 'center',
                fontSize: '1.25rem',
                color: '#FFD700',
                fontWeight: 700,
                letterSpacing: '0.03em',
              }}>
                {retrogradeData.totalRetrogrades} Active Retrogrades
              </div>
              <div style={{
                flex: 1,
                padding: '1rem',
                borderRadius: '12px',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                fontSize: '1.1rem',
                color: '#fff',
                fontWeight: 500,
              }}>
                <span style={{ opacity: 0.7 }}>Time</span> <span style={{ fontWeight: 700 }}>{retrogradeData.currentTime}</span>
              </div>
            </div>
          </div>

          <div className="ascendant-description">
            <h3>Planetary Retrograde Status</h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1.2rem',
              marginTop: '1.2rem',
            }}>
              {displayPairs.map((pair, rowIdx) => (
                <React.Fragment key={rowIdx}>
                  {pair.map((planetName, colIdx) => {
                    const planet = retroByName[planetName];
                    if (!planet) return <div key={planetName}></div>;
                    // Border color logic
                    let borderColor = 'rgba(255, 255, 255, 0.08)';
                    if (planet.isRetrograde) borderColor = 'rgba(255,68,68,0.4)'; // red, 40% opacity
                    else borderColor = 'rgba(182,233,123,0.4)'; // subtle green-gold blend, 40% opacity

                    // Mars icon always the Mars symbol
                    const planetIcon = planet.planet === 'Mars' ? '♂️' : planet.icon;

                    return (
                      <motion.div
                        key={planet.planet}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: (rowIdx*2+colIdx) * 0.1, duration: 0.4 }}
                        style={{
                          background: 'rgba(25, 25, 25, 0.8)',
                          border: `1px solid ${borderColor}`,
                          borderRadius: '12px',
                          padding: '1.25rem',
                          marginBottom: '0.85rem',
                          position: 'relative',
                          transition: 'all 0.3s ease',
                          minHeight: 64
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <span style={{ 
                              fontSize: '2.5rem',
                              filter: planet.isRetrograde ? 'drop-shadow(0 0 8px #FF4444)' : 'drop-shadow(0 0 8px #b6e97b)'
                            }}>{planetIcon}</span>
                            <div>
                              <h4 style={{ 
                                color: '#FFD700',
                                margin: 0,
                                fontSize: '1.3rem',
                                fontWeight: 'bold'
                              }}>
                                {planet.planet}
                              </h4>
                              <p style={{ 
                                margin: '0.25rem 0 0 0',
                                color: planet.isRetrograde ? '#FF4444' : '#b6e97b',
                                fontSize: '1rem',
                                fontWeight: '700',
                                letterSpacing: '0.03em',
                                textShadow: planet.isRetrograde ? '0 0 4px #ff4444aa' : '0 0 4px #b6e97baa'
                              }}>
                                {planet.isRetrograde ? 'RETROGRADE' : 'DIRECT'}
                              </p>
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', minWidth: 120, display: 'flex', alignItems: 'center', gap: 8 }}>
                            {(planet.isRetrograde && (planet.startDate || planet.endDate)) ? (
                              <div style={{ fontSize: '0.9rem', color: '#FFC107', fontWeight: 600 }}>
                                {planet.startDate ? planet.startDate : 'Unknown'}
                                {(planet.startDate && planet.endDate) ? ' - ' : ''}
                                {planet.endDate ? planet.endDate : (planet.isRetrograde ? 'Ongoing' : 'Unknown')}
                              </div>
                            ) : (
                              <div style={{ fontSize: '0.9rem', color: '#FFC107', fontWeight: 600 }}>
                                {planet.isRetrograde ? 'Ongoing' : (nextRetrogrades[planet.planet] ? `Next: ${nextRetrogrades[planet.planet]}` : '')}
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default RetrogradeExperience; 
